import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Briefcase, Clock, MapPin, ArrowRight, Edit2, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';

const TalentDashboard = () => {
  const { profile, user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: 'Dashboard | Talent Collective',
    description: 'View your profile status, applications, and discover new opportunities.'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/applications`),
        axios.get(`${API}/projects`)
      ]);
      setApplications(appsRes.data);
      setRecentProjects(projectsRes.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProfileCompletion = () => {
    if (!profile) return 0;
    const fields = ['name', 'headline', 'bio', 'linkedin_url', 'resume_url', 'function', 'experience_years', 'availability'];
    const filled = fields.filter(f => profile[f]).length;
    return Math.round((filled / fields.length) * 100);
  };

  const statusCounts = applications.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});

  const statusColors = {
    submitted: 'badge-neutral',
    under_review: 'badge-warning',
    shortlisted: 'badge-primary',
    intro_scheduled: 'badge-success',
    not_selected: 'badge-error',
  };

  const statusIcons = {
    submitted: Clock,
    under_review: AlertCircle,
    shortlisted: CheckCircle2,
    intro_scheduled: CheckCircle2,
    not_selected: XCircle,
  };

  const functionColors = {
    product: 'bg-teal-100 text-teal-700',
    design: 'bg-violet-100 text-violet-700',
    engineering: 'bg-blue-100 text-blue-700',
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="talent-dashboard">
      <Header />

      <div className="container-custom px-4 sm:px-6 py-8 sm:py-12">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Hi, {profile?.name?.split(' ')[0] || 'there'}.</h1>
          <p className="text-sm sm:text-base text-slate-600">Here's what's happening with your applications.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Profile Status */}
            <Card className="p-4 sm:p-6 overflow-hidden" data-testid="profile-status-card">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold mb-1">Profile Status</h3>
                  <p className="text-xs sm:text-sm text-slate-500">
                    Your profile is live. Start browsing and applying to projects.
                  </p>
                </div>
                <span className="badge-success flex-shrink-0 self-start">
                  Active
                </span>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Profile completion</span>
                  <span className="font-medium">{calculateProfileCompletion()}%</span>
                </div>
                <Progress value={calculateProfileCompletion()} className="h-2" />
              </div>

              <Link to="/profile/edit">
                <Button variant="outline" className="w-full" data-testid="edit-profile-btn">
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              </Link>
            </Card>

            {/* Applications Summary */}
            <Card className="p-4 sm:p-6 overflow-hidden" data-testid="applications-summary">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold">Your Applications</h3>
                <Link to="/applications" className="text-teal-600 text-sm font-medium hover:underline">
                  View all
                </Link>
              </div>

              {applications.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-900">{applications.length}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{statusCounts.under_review || 0}</p>
                      <p className="text-xs text-slate-500">In Review</p>
                    </div>
                    <div className="text-center p-3 bg-teal-50 rounded-lg">
                      <p className="text-2xl font-bold text-teal-600">{statusCounts.shortlisted || 0}</p>
                      <p className="text-xs text-slate-500">Shortlisted</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">{statusCounts.intro_scheduled || 0}</p>
                      <p className="text-xs text-slate-500">Intro Scheduled</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {applications.slice(0, 3).map((app) => {
                      const StatusIcon = statusIcons[app.status] || Clock;
                      return (
                        <div key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{app.project_title}</p>
                            <p className="text-sm text-slate-500">{app.company_name}</p>
                          </div>
                          <span className={`${statusColors[app.status]} flex items-center capitalize`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {app.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No applications yet.</p>
                  <Link to="/projects">
                    <Button className="btn-primary" data-testid="browse-projects-btn">
                      Browse Opportunities
                    </Button>
                  </Link>
                </div>
              )}
            </Card>

            {/* Recommended Projects */}
            <Card className="p-6" data-testid="recommended-projects">
              <div className="flex items-center justify-between mb-6">
                <h3 className="heading-4">Projects for You</h3>
                <Link to="/projects" className="text-teal-600 text-sm font-medium hover:underline">
                  Browse Opportunities
                </Link>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="animate-pulse p-4 bg-slate-50 rounded-lg">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : recentProjects.length > 0 ? (
                <div className="space-y-4">
                  {recentProjects.map((project) => (
                    <Link to={`/projects/${project.id}`} key={project.id}>
                      <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-slate-900">{project.title}</h4>
                            <p className="text-sm text-slate-500">{project.company_name}</p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-slate-400" />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`${functionColors[project.function]} px-2 py-0.5 rounded-full text-xs capitalize`}>
                            {capitalize(project.function)}
                          </span>
                          <span className="badge-neutral flex items-center text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {project.hours_per_week}h/week
                          </span>
                          <span className="badge-neutral flex items-center text-xs capitalize">
                            <MapPin className="h-3 w-3 mr-1" />
                            {capitalize(project.location_type)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">No projects available.</p>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Card */}
            <Card className="p-6" data-testid="profile-card">
              <div className="text-center">
                <div className="h-20 w-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                  {profile?.photo_url ? (
                    <img src={profile.photo_url} alt="" className="h-20 w-20 rounded-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-teal-700">
                      {profile?.name?.[0] || user?.email?.[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-slate-900">{profile?.name || 'Your Name'}</h3>
                <p className="text-sm text-slate-500 mb-4">{profile?.headline || 'Add a headline.'}</p>
                
                {profile?.function && (
                  <span className={`${functionColors[profile.function]} px-3 py-1 rounded-full text-sm capitalize`}>
                    {capitalize(profile.function)}
                  </span>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Experience</span>
                  <span className="text-slate-900">
                    {profile?.experience_years <= 5 ? '0-5 years' :
                     profile?.experience_years <= 12 ? '5-12 years' :
                     profile?.experience_years <= 20 ? '12-20 years' : '20+ years'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Availability</span>
                  <span className="text-slate-900">{profile?.availability || 0} hrs/week</span>
                </div>
              </div>
            </Card>

            {/* Quick Links */}
            <Card className="p-6" data-testid="quick-links">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/projects">
                  <Button variant="ghost" className="w-full justify-start">
                    <Briefcase className="h-4 w-4 mr-2" />
                    Browse Opportunities
                  </Button>
                </Link>
                <Link to="/applications">
                  <Button variant="ghost" className="w-full justify-start">
                    <Clock className="h-4 w-4 mr-2" />
                    My Applications
                  </Button>
                </Link>
                <Link to="/profile/edit">
                  <Button variant="ghost" className="w-full justify-start">
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TalentDashboard;
